/**
 * engine/video.ts — the one module that drives the clip players and writes the player half of
 * the «видео» slice.
 *
 * Tasks 13 and 14. It sits between four things and lets none of them know about the others:
 * the «Видео» tab (which clip, which place on the page), YouTube's player (`engine/youtube.ts`,
 * the seam), the one video element of the mp4s (`video/element.ts`) and the music
 * (`engine/player.ts`). What it decides is `spec.md` «Видео»:
 *
 * - **«Старт любого видео ставит музыку на паузу».** The clip starting — YouTube's player says
 *   `buffering` or `playing`, or the video element says `playing` — pauses the music if it is
 *   sounding, whether we asked or the listener tapped the player's own button. A pause, not a
 *   stop: the track keeps its second, and «играть» on the mini player carries on from it.
 * - **«Старт музыки останавливает видео».** The music going from silent to sounding — a tap on
 *   a track, on the mini player, on the lock screen — pauses the clip, whichever player has it.
 * - **«После окончания видео музыка не возобновляется сама».** Nothing here ever calls the
 *   music's `play`. The end of a clip starts the next clip, never the music.
 * - **«Уход с вкладки „Видео“ останавливает ролик».** The tab leaving calls {@link
 *   VideoController.detach}: YouTube's frame is taken out of the page with its sound, and the
 *   video element is paused, let go of its clip and taken out too.
 * - **«Экран блокировки и пластинка к видео не относятся».** Nothing here touches the media
 *   session or the record; the lock screen goes on showing the track.
 *
 * One clip at a time: {@link VideoController.play} replaces the clip in the player area. The area
 * holds ONE of the two players — YouTube's frame for a link, the video element for an mp4 — and
 * the other is taken out, silent, before it is put in.
 *
 * ## Clips in a row (task 75)
 *
 * The list plays in a row, down from newer to older (`store/video.ts`, `neighbours`): a clip
 * ending starts the next one, and «Дальше» and «Назад» move along the list. The probe of task 74
 * answered for the owner's iPhone: a link following a link goes into the same frame by `load`,
 * started by nothing but the end of the last one, and the phone lets it play with its sound
 * (`.omc/wiki/proba-roliki-podryad.md`). While the tab is on screen the list loads on whenever
 * the clip shown is the last loaded — so the next has a name under the frame, and the end of a
 * clip finds it waiting; the end of the whole list is a stop.
 *
 * Between two clips the slice says `starting`, not `idle`: the gold «играть» of task 72 does not
 * blink over every change of clip, and the music started in that gap stops the clip on its way —
 * «старт музыки останавливает видео», a clip not yet sounding included. A start the phone refuses
 * gets the gold «играть» for the tap: at once when YouTube says `blocked`, and {@link
 * START_WAIT_MS} after the start was asked for when it says nothing, or only «buffering».
 *
 * Nothing goes on by itself away from the tab, or over the music: a next clip waiting for its
 * page is dropped when the tab is left, and the end of a clip starts none while the music plays.
 *
 * ## The field over the list (task 76)
 *
 * What plays in a row is what the list shows (`store/video.ts`, `playlist`): the clips the field
 * found while it has an answer. That answer is all of them, so nothing loads on behind it; a clip
 * playing on from before the search is followed by the first one found. Typing reaches no player
 * here: the clip in the frame plays on.
 *
 * Task 77: the same for «Избранные» — the clips with a heart (`store/favorites.ts`), newest heart
 * first. A heart taken off takes its clip out of what plays; the clip in the frame plays on, and
 * the one after it is next (`store/video.ts`, `aroundIn`).
 *
 * ## The video element, and the tap
 *
 * The element is one for the life of the app (`video/element.ts`), and a tap on an mp4 starts it
 * inside the tap: a phone lets a gesture start a sound and refuses a callback. That holds for a
 * clip tapped in «Поиск» too, where the tab does not exist yet — the element plays off the page,
 * and {@link VideoController.attach} puts it on the page as it is. A start the phone refused all
 * the same leaves the element with its own play button on screen, and says nothing.
 */

import type { VideoItem } from '../lib/api';
import { isSounding, usePlayer } from '../store/player';
import {
  aroundIn,
  aroundShown,
  isPlayable,
  isYoutubeClip,
  playlistNow,
  shownClip,
  useVideo,
} from '../store/video';
import type { Mp4Clip, VideoPlayback, YoutubeClip } from '../store/video';
import { clipSurface } from '../video/element';
import type { ClipSurface } from '../video/element';

import { player } from './player';
import { youtube } from './youtube';
import type { YoutubeEvent, YoutubeFactory, YoutubePlayer } from './youtube';

export interface VideoController {
  /**
   * The tab is on screen with `video` to show: its player goes into `host`, showing it. The
   * clip starts too if it was tapped before the tab existed — in the results of search.
   */
  attach(host: HTMLElement, video: VideoItem): void;
  /** The tab is leaving: the clip stops, and its player goes with it. */
  detach(): void;
  /** A tap on a clip: it becomes the one shown, and starts. */
  play(video: VideoItem): void;
  /** Task 75: «Дальше», and the end of a clip — the next clip of the list, started. */
  next(): void;
  /** Task 75: «Назад» — the clip before the one shown, started. */
  previous(): void;
  dispose(): void;
}

export interface VideoDeps {
  readonly youtube: () => YoutubeFactory;
  /** The one video element of the mp4s. The real one is `video/element.ts`'s. */
  readonly clip: () => ClipSurface;
  /** Pauses the music. The real one is the music player's `pause`. */
  readonly pauseMusic: () => void;
}

/**
 * Task 75: a clip asked to start that has not reached «playing» this long after gets the gold
 * «играть» — a refusal that says nothing, or a network too slow to wait for. The eight seconds
 * of `spec.md` «Видео» for YouTube that does not come at all.
 */
export const START_WAIT_MS = 8000;

/** The states that mean the clip is making, or about to make, a sound. */
function clipSounding(playback: VideoPlayback): boolean {
  return playback === 'playing' || playback === 'buffering' || playback === 'starting';
}

/** What the video element's own events say about the clip, by the slice's words. */
const ELEMENT_PLAYBACK: ReadonlyMap<string, VideoPlayback> = new Map([
  ['playing', 'playing'],
  ['waiting', 'buffering'],
  ['pause', 'paused'],
  ['ended', 'ended'],
]);

export function createVideoController(deps: VideoDeps): VideoController {
  const set = useVideo.setState;
  const surface = deps.clip();
  /** Where the tab's player area is, while the tab is on screen. */
  let host: HTMLElement | undefined;
  /** YouTube's player, while a link is in the area. */
  let frame: YoutubePlayer | undefined;
  /** The mp4 the video element holds, while it holds one — on the page or, from search, off it. */
  let mp4: number | undefined;
  /** A YouTube clip tapped while there was no frame yet: it starts when the frame comes. */
  let startWhenAttached: number | undefined;
  /** A start asked for and not yet heard. See {@link START_WAIT_MS}. */
  let startWatch: ReturnType<typeof setTimeout> | undefined;
  /** How many times the tab has been left: a next clip waiting for a page from before is dropped. */
  let departures = 0;

  function onYoutube(event: YoutubeEvent): void {
    switch (event.type) {
      case 'ready':
        set({ frame: 'ready' });
        return;
      case 'unavailable':
        set({ frame: 'unavailable' });
        return;
      case 'refused':
        set({ problem: 'refused', playback: 'idle' });
        return;
      case 'failed':
        set({ problem: 'failed', playback: 'idle' });
        return;
      case 'blocked':
        // The phone refused a start, as a next clip with no finger behind it may be: the clip is
        // not going, whatever the player said last, and the gold «играть» goes up for the tap.
        set({ playback: 'idle' });
        return;
      case 'state': {
        const playback: VideoPlayback =
          event.state === 'cued' || event.state === 'unstarted' ? 'idle' : event.state;
        // «I have the clip» is no answer to a start asked for: it is still starting.
        if (playback === 'idle' && useVideo.getState().playback === 'starting') return;
        clipState(playback);
        return;
      }
    }
  }

  /**
   * A player's word about the clip: written down, and a start pauses the music. The end of a
   * clip goes on to the next one of the list.
   */
  function clipState(playback: VideoPlayback): void {
    set({ playback, ...(clipSounding(playback) ? { problem: undefined } : {}) });
    if (playback === 'playing') unwatchStart();
    if (clipSounding(playback) && isSounding(usePlayer.getState().status)) deps.pauseMusic();
    if (playback === 'ended') void advance(true);
  }

  /** See {@link START_WAIT_MS}. */
  function watchStart(): void {
    unwatchStart();
    startWatch = setTimeout(() => {
      startWatch = undefined;
      const { playback } = useVideo.getState();
      if (playback === 'starting' || playback === 'buffering') set({ playback: 'idle' });
    }, START_WAIT_MS);
  }

  function unwatchStart(): void {
    clearTimeout(startWatch);
    startWatch = undefined;
  }

  // The element's events count only while it holds a clip: letting go of one pauses it, and
  // that pause is ours, not the listener's.
  const onElement = (event: Event): void => {
    if (mp4 === undefined) return;
    if (event.type === 'error') {
      set({ problem: 'failed', playback: 'idle' });
      return;
    }
    const playback = ELEMENT_PLAYBACK.get(event.type);
    if (playback !== undefined) clipState(playback);
  };
  const ELEMENT_EVENTS = [...ELEMENT_PLAYBACK.keys(), 'error'];
  for (const type of ELEMENT_EVENTS) surface.element.addEventListener(type, onElement);

  // «Старт музыки»: silent → sounding, and only that edge. A track that is already playing
  // while a clip is paused is not a start, and must not reach into a player nobody touched.
  const unsubscribe = usePlayer.subscribe((next, previous) => {
    if (!isSounding(next.status) || isSounding(previous.status)) return;
    if (!clipSounding(useVideo.getState().playback)) return;
    frame?.pause();
    if (mp4 !== undefined) surface.element.pause();
  });

  /** YouTube's frame out of the page, and its sound with it. */
  function dropFrame(): void {
    frame?.destroy();
    frame = undefined;
  }

  /** The video element silent, emptied of its clip, and out of the page. */
  function dropMp4(): void {
    if (mp4 === undefined) return;
    mp4 = undefined;
    const { element, node } = surface;
    element.pause();
    // The standard way to let go of a clip: no source, and a load of nothing, so the decoder
    // and the connection are released rather than kept for a clip nobody will play.
    element.removeAttribute('src');
    element.load();
    node.remove();
  }

  /** The mp4 into the element — and onto the page, when there is a page for it. */
  function showMp4(video: Mp4Clip, start: boolean): void {
    const { element, node } = surface;
    // A clip that failed is told its address again: an element in error does not retry.
    if (mp4 !== video.id || element.error !== null) {
      if (video.cover === null) element.removeAttribute('poster');
      else element.poster = video.cover;
      element.src = video.stream.url;
      mp4 = video.id;
    }
    if (host !== undefined && node.parentNode !== host) host.append(node);
    set({ frame: 'ready' });
    // Refused — typically a start with no tap behind it — the element's own button is there.
    if (start) void element.play().catch(() => undefined);
  }

  /** YouTube's frame for `video`, made in the area if there is none; started when `start`. */
  function showYoutube(video: YoutubeClip, start: boolean): void {
    if (host === undefined) {
      startWhenAttached = start ? video.id : undefined;
      return;
    }
    if (frame === undefined) {
      set({ frame: 'loading' });
      frame = deps.youtube()({ host, videoId: video.youtube.id, onEvent: onYoutube });
      if (!start) return;
    }
    frame.load(video.youtube.id);
  }

  function detach(): void {
    unwatchStart();
    dropFrame();
    dropMp4();
    set({ frame: 'idle', playback: 'idle', problem: undefined });
  }

  /** `video` becomes the one shown, and starts. */
  function play(video: VideoItem): void {
    if (!isPlayable(video)) return;
    set({ selected: video, playback: 'starting', problem: undefined });
    watchStart();
    if (isYoutubeClip(video)) {
      dropMp4();
      showYoutube(video, true);
    } else {
      dropFrame();
      showMp4(video, true);
    }
  }

  /**
   * While the tab is on screen: the next page, when the clip shown is the last loaded or not
   * among the loaded at all. Each page that lands asks again, until the clip has a next or the
   * list has no more.
   */
  function loadAhead(): void {
    const state = useVideo.getState();
    if (host === undefined || playlistNow().complete || state.loadingMore) return;
    if (shownClip(state) === undefined || aroundShown().next !== undefined) return;
    void state.loadMore();
  }

  // A page landed, another clip shown — or the field emptied, or «Все» chosen again: the whole
  // list plays again, and the clip shown may be the last of its loaded pages.
  const unsubscribeList = useVideo.subscribe((now, before) => {
    const changed =
      now.items !== before.items ||
      now.selected !== before.selected ||
      now.search !== before.search ||
      now.view !== before.view;
    if (changed) loadAhead();
  });

  /**
   * The clip after the one shown, started: «Дальше», or — `ended` — the end of a clip. Not loaded
   * yet, it waits for the pages it needs. A clip chosen meanwhile (a tap on a row) wins, the tab
   * left drops it, and a clip that ended never starts the next one over the music.
   */
  async function advance(ended: boolean): Promise<void> {
    const from = shownClip(useVideo.getState());
    const visit = departures;
    if (from === undefined) return;
    for (;;) {
      const state = useVideo.getState();
      if (host === undefined || departures !== visit || shownClip(state)?.id !== from.id) return;
      const list = playlistNow();
      const { next } = aroundIn(list, from);
      if (next !== undefined) {
        if (ended && isSounding(usePlayer.getState().status)) return;
        play(next);
        return;
      }
      // The end of what plays; or the page did not come (the network) — «Дальше» tries again.
      if (list.complete || !(await state.loadMore())) return;
    }
  }

  return {
    attach(area, video) {
      if (!isPlayable(video)) return;
      host = area;
      const start = startWhenAttached === video.id;
      startWhenAttached = undefined;
      set({ selected: video, problem: undefined });
      if (isYoutubeClip(video)) {
        dropMp4();
        dropFrame();
        set({ playback: start ? 'starting' : 'idle' });
        if (start) watchStart();
        showYoutube(video, start);
      } else {
        dropFrame();
        // A clip started from search keeps playing: it is put on the page as it is.
        if (mp4 !== video.id) set({ playback: 'idle' });
        showMp4(video, false);
      }
      loadAhead();
    },

    detach() {
      startWhenAttached = undefined;
      host = undefined;
      departures += 1;
      detach();
    },

    play,

    next() {
      void advance(false);
    },

    previous() {
      const { previous } = aroundShown();
      if (previous !== undefined) play(previous);
    },

    dispose() {
      unsubscribe();
      unsubscribeList();
      for (const type of ELEMENT_EVENTS) surface.element.removeEventListener(type, onElement);
      host = undefined;
      detach();
    },
  };
}

/* ────────────────────────────────────────────────────────────────────────────
 * The instance the app uses
 * ──────────────────────────────────────────────────────────────────────────── */

let instance: VideoController | undefined;

/** The controller, created on first use with the real players and the real music player. */
export function video(): VideoController {
  instance ??= createVideoController({
    youtube,
    clip: () => clipSurface,
    pauseMusic: () => {
      player().pause();
    },
  });
  return instance;
}

/**
 * Replaces the controller — for a test that installs its own players. Resets the player half of
 * the slice, so one test's clip does not leak into the next.
 */
export function installVideo(deps: VideoDeps): VideoController {
  instance?.dispose();
  useVideo.setState({ selected: undefined, frame: 'idle', playback: 'idle', problem: undefined });
  instance = createVideoController(deps);
  return instance;
}
